
































































import Vue from 'vue';
import 'pepjs';

import {
  commitFoundWord,
  dispatchClosePath,
  dispatchCreateWordSearch,
  dispatchStartPath,
  dispatchUpdatePath,
  readBuildingPuzzle,
  readCandidatePath,
  readErrored,
  readFoundWords,
  readLetterGrid,
  readPuzzle,
  readStartedAt,
  readWon,
  readWordList,
  readWordPaths,
} from '@/store';
import { Store } from 'vuex';
import { GridState, WordList, LetterGrid, WordPath, WordPathPosition, Puzzle } from '@/game';
import PathVisualization from './PathVisualization.vue';
import PlayTimer from './PlayTimer.vue';
import some from 'lodash/some';
import find from 'lodash/find';
import findIndex from 'lodash/findIndex';

declare var ConfettiGenerator: any;
import 'confetti-js';

interface ComponentData {
  pathing: boolean;
  path: WordPath;
  confetti: any;
  tileSize: number;
}

export default Vue.extend({
  name: 'Game',
  components: {
    PathVisualization,
    PlayTimer,
  },
  props: {
    seed: {
      type: Number,
      required: false,
    },
  },
  filters: {
    json(input: any) {
      return JSON.stringify(input);
    },
  },
  data(): ComponentData {
    return {
      pathing: false,
      path: [],
      confetti: null,
      tileSize: 52,
    };
  },
  computed: {
    puzzle(): Puzzle | null {
      return readPuzzle(this.$store);
    },
    words(): WordList {
      return readWordList(this.$store);
    },
    grid(): LetterGrid {
      return readLetterGrid(this.$store);
    },
    paths(): WordPath[] {
      return readWordPaths(this.$store);
    },
    candidate(): WordPath {
      return readCandidatePath(this.$store);
    },
    found(): { [key: string]: WordPath } {
      return readFoundWords(this.$store);
    },
    totalFound(): number {
      return Object.keys(this.found).length;
    },
    building(): boolean {
      return readBuildingPuzzle(this.$store);
    },
    errored(): boolean {
      return readErrored(this.$store);
    },
    won(): boolean {
      return readWon(this.$store);
    },
    candidateWord(): string {
      return this.candidate && this.candidate.map((p) => p.char).join('');
    },
    startedAt(): Date {
      return readStartedAt(this.$store);
    },
    listeners(): object {
      return {
        pointerdown: this.startPath,
        pointermove: this.updatePath,
        pointerup: this.closePath,
      };
    },
  },
  mounted() {
    this.newPuzzle();
  },
  watch: {
    won: {
      handler() {
        if (this.won) {
          this.confetti = new ConfettiGenerator({
            target: 'confetti-holder',
            max: 200,
          });
          this.confetti.render();
        } else {
          if (this.confetti) {
            this.confetti.clear();
          }
        }
      },
    },
  },
  methods: {
    eventToCoordinate(event: PointerEvent) {
      // Get indexed position (x, y) relative to top-left of game table
      //
      const tileSize = this.tileSize;
      const target = event.target as HTMLTableElement;
      const offsetX = event.pageX - target.offsetLeft;
      const offsetY = event.pageY - target.offsetTop;
      const x = Math.floor(offsetX / tileSize);
      const y = Math.floor(offsetY / tileSize);

      // Check that the cursor is within a circle of radius R around the
      // center of the tile at indexed position (x, y)
      //
      const tileX = x * tileSize;
      const tileY = y * tileSize;
      const centerX = tileX + 0.5 * tileSize;
      const centerY = tileY + 0.5 * tileSize;
      const distance = Math.sqrt(Math.pow(centerX - offsetX, 2) + Math.pow(centerY - offsetY, 2));

      if (distance < 0.5 * tileSize) {
        return { x, y };
      }
    },
    startPath(event: PointerEvent) {
      event.preventDefault();

      const coordinates = this.eventToCoordinate(event);
      if (coordinates) {
        const { x, y } = coordinates;
        const char = this.grid[y][x];
        dispatchStartPath(this.$store, { x, y, char });
      }
    },
    updatePath(event: PointerEvent) {
      event.preventDefault();

      const coordinates = this.eventToCoordinate(event);
      if (coordinates) {
        const { x, y } = coordinates;
        const char = this.grid[y][x];
        dispatchUpdatePath(this.$store, { x, y, char });
      }
    },
    closePath(event: PointerEvent) {
      event.preventDefault();

      dispatchClosePath(this.$store);
    },
    cellClass(x: number, y: number): string | undefined {
      const inPath = (path: WordPath) => some(path, (p) => p.x === x && p.y === y);
      const isFound = some(this.found, (path, foundWord) => inPath(path));

      if (isFound) {
        return 'game--found-char';
      }
    },
    transitionDelay(x: number, y: number): object | undefined {
      const inPath = (path: WordPath) => some(path, (p) => p.x === x && p.y === y);
      const foundWord = find(this.found, (path, _) => inPath(path));
      const index = findIndex(foundWord, (p) => p.x === x && p.y === y);

      if (foundWord) {
        return {
          animationDelay: `${index * 50}ms`,
        };
      }
    },
    listClass(word: string): string[] {
      const classes = [
        'game--word',
      ];
      const found = some(this.found, (path, foundWord) => foundWord.toLowerCase() === word.toLowerCase());
      if (found) {
        classes.push('game--found-word');
      }

      return classes;
    },
    newPuzzle(): void {
      // Determine puzzle size based on UA dimensions. Work around Cypress' handling of pixel ratio.
      //
      const pixelRatio = 1;
      const maxCells = (size: number) => Math.min(Math.floor((size - 40) / this.tileSize), 12); // upper cap at 12x12
      const width = maxCells(window.innerWidth / pixelRatio);
      const height = maxCells(window.innerHeight / pixelRatio - 150); // 150px buffer for header and word list
      const wordCount = 3 + Math.floor(width * height / 15);

      dispatchCreateWordSearch(this.$store, {
        seed: this.seed,
        width,
        height,
        wordCount,
      });
    },
  },
});
